import type { FormValue } from "../../../redux/interfaces/folder.types";
import { DynamicFormBuilder } from "../forms/DynamicFormBuilder";
import { allFormsValidators } from "../../../utils/vault/forms/allFormsValidators";

type FormFieldValue = string | undefined | null;

export interface DocumentMetadataState {
  values: Record<string, FormFieldValue>;
  requiredFields: Record<string, boolean>;
  displayFields: Record<string, "block" | "hidden">;
  labelFields: Record<string, string>;
}

interface DocumentMetadataFieldsProps {
  fields: FormValue[];
  state: DocumentMetadataState;
  errors: Record<string, string>;
  onChange: (key: string, value: FormFieldValue) => void;
  onBlur?: (key: string) => void;
  disabled?: boolean;
}

export function getVisibleDocumentMetadataFields(fields: FormValue[]) {
  return [...fields]
    .filter((field) => !field.hidden)
    .sort((a, b) => a.order - b.order);
}

export function createDocumentMetadataState(
  fields: FormValue[],
): DocumentMetadataState {
  const values: Record<string, FormFieldValue> = {};
  const requiredFields: Record<string, boolean> = {};
  const displayFields: Record<string, "block" | "hidden"> = {};
  const labelFields: Record<string, string> = {};

  fields.forEach((field) => {
    values[field.key] = field.value === "" ? undefined : field.value;
    requiredFields[field.key] = field.required;
    displayFields[field.key] = field.hidden ? "hidden" : "block";
    labelFields[field.key] = field.label;
  });

  return {
    values,
    requiredFields,
    displayFields,
    labelFields,
  };
}

export async function validateDocumentMetadata(
  fields: FormValue[],
  state: DocumentMetadataState,
): Promise<Record<string, string>> {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const isVisible = state.displayFields[field.key] !== "hidden";
    if (!isVisible) continue;

    const value = state.values[field.key];
    const isRequired = state.requiredFields[field.key] ?? field.required;

    if (isRequired && (value === undefined || value === "" || value === null)) {
      errors[field.key] = "Campo requerido";
      continue;
    }

    const validator = allFormsValidators[field.key];
    if (validator && value) {
      try {
        await validator(null, value);
      } catch (error: any) {
        errors[field.key] = error?.message || "Campo inválido";
      }
    }
  }

  return errors;
}

export function mapDocumentMetadataFormValues(
  fields: FormValue[],
  values: Record<string, FormFieldValue>,
) {
  return fields.map((field) => ({
    ...field,
    value: values[field.key] ?? field.value ?? "",
  }));
}

export function DocumentMetadataFields({
  fields,
  state,
  errors,
  onChange,
  onBlur,
  disabled = false,
}: DocumentMetadataFieldsProps) {
  const visibleFields = getVisibleDocumentMetadataFields(fields);

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap -mx-2">
      {visibleFields.map((field) => (
        <DynamicFormBuilder
          key={field.key}
          formItem={field}
          value={state.values[field.key]}
          onChange={onChange}
          onBlur={onBlur}
          requiredFields={state.requiredFields}
          displayFields={state.displayFields}
          labelFields={state.labelFields}
          disabled={disabled}
          errors={errors}
        />
      ))}
    </div>
  );
}
